import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAdminRealtime } from '../context/AdminRealtimeContext';
import {
  IconLayoutGrid,
  IconLayers,
  IconUtensils,
  IconShoppingBag,
  IconHistory,
  IconCreditCard,
  IconStar,
  IconMessageSquare,
  IconLifeBuoy,
  IconGift,
  IconUsers,
  IconShield,
  IconFileText,
  IconTrendingUp,
  IconChevronDown,
  IconLogOut,
  IconSpeakerMute,
  IconSpeakerOn,
  IconImage,
} from './Icons';
import { BrandLogo } from './BrandLogo';
import { StoreOpenSwitch } from './StoreOpenSwitch';

const navSections = [
  {
    label: 'Overview',
    items: [{ to: '/', label: 'Dashboard', end: true, Icon: IconLayoutGrid }],
  },
  {
    label: 'Menu & orders',
    items: [
      { to: '/categories', label: 'Categories', Icon: IconLayers },
      { to: '/menu-items', label: 'Menu items', Icon: IconUtensils },
      { to: '/orders', label: 'Active orders', Icon: IconShoppingBag },
      { to: '/order-history', label: 'Order history', Icon: IconHistory },
    ],
  },
  {
    label: 'Payments',
    items: [{ to: '/payments', label: 'Payment history', Icon: IconCreditCard }],
  },
  {
    label: 'Analytics',
    items: [{ to: '/analytics', label: 'Analytics', Icon: IconTrendingUp }],
  },
  {
    label: 'Customers',
    items: [
      { to: '/reviews', label: 'Reviews', Icon: IconStar },
      { to: '/feedback', label: 'Feedback', Icon: IconMessageSquare },
      { to: '/support-tickets', label: 'Support', Icon: IconLifeBuoy },
      { to: '/app-users', label: 'App users', Icon: IconUsers },
    ],
  },
  {
    label: 'Store',
    items: [
      { to: '/banners', label: 'Banners', Icon: IconImage },
      { to: '/rewards', label: 'Rewards', Icon: IconGift },
      { to: '/metadata', label: 'Metadata', Icon: IconShield },
      { to: '/legal-pages', label: 'Legal pages', Icon: IconFileText },
    ],
  },
];

function adminPhotoUrl(admin) {
  if (!admin || typeof admin !== 'object') return '';
  return (
    admin.avatarUrl ||
    admin.photoUrl ||
    admin.profileImageUrl ||
    admin.imageUrl ||
    admin.picture ||
    ''
  );
}

function initialsFromAdmin(admin) {
  const name = (admin?.name || admin?.email || '?').trim();
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase() || '?';
}

export function AppLayout() {
  const { admin, logout } = useAuth();
  const { connectionState, soundMuted, soundBlocked, toggleSoundMuted, testOrderSound } = useAdminRealtime();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    function handlePointerDown(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') setUserMenuOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [userMenuOpen]);

  async function handleLogout() {
    setUserMenuOpen(false);
    await logout();
    navigate('/login', { replace: true });
  }

  const displayName = admin?.name || admin?.email || 'Admin';
  const photo = adminPhotoUrl(admin);
  const initials = useMemo(() => initialsFromAdmin(admin), [admin]);

  return (
    <div className="container-fluid app-layout p-0">
      <div className="row flex-nowrap app-layout-row g-0">
        <aside className="app-sidebar flex-shrink-0 d-flex flex-column" aria-label="Main navigation">
          <div className="app-sidebar-brand">
            <div className="app-sidebar-brand-inner">
              <BrandLogo className="app-sidebar-brand-mark" alt="" />
              <div className="min-w-0">
                <div className="brand-title app-sidebar-brand-title lh-sm">Pizza Point</div>
                <div className="app-sidebar-brand-sub font-sans">Admin console</div>
              </div>
            </div>
          </div>
          <nav className="app-sidebar-nav flex-grow-1 overflow-auto">
            {navSections.map((section) => (
              <div key={section.label} className="app-sidebar-section">
                <div className="app-sidebar-section-label">{section.label}</div>
                <ul className="app-sidebar-list list-unstyled mb-0">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          `app-sidebar-link${isActive ? ' is-active' : ''}`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <span
                              className={`app-sidebar-link-icon${isActive ? ' is-active' : ''}`}
                              aria-hidden
                            >
                              <item.Icon size={18} strokeWidth={isActive ? 2.25 : 2} />
                            </span>
                            <span className="app-sidebar-link-label">{item.label}</span>
                            {isActive ? (
                              <span className="app-sidebar-link-glow" aria-hidden />
                            ) : null}
                          </>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
          <footer className="app-sidebar-footer font-sans">
            <span className="app-sidebar-footer-dot" aria-hidden />
            Question Mark Pizza Point
          </footer>
        </aside>
        <main className="col p-0 d-flex flex-column bg-light-subtle min-w-0 app-layout-main">
          <header className="app-topbar border-bottom bg-white px-3 px-lg-4 py-2 d-flex align-items-center justify-content-between flex-shrink-0 shadow-sm">
            <div className="app-topbar-start d-flex align-items-center gap-2 flex-wrap min-w-0">
              <span className={`badge realtime-connection-badge ${connectionState === 'connected' ? 'realtime-live' : connectionState === 'reconnecting' ? 'realtime-reconnecting' : 'realtime-offline'}`}>
                {connectionState === 'connected' ? 'Live' : connectionState === 'reconnecting' ? 'Reconnecting' : 'Offline'}
              </span>
              <div className="realtime-sound-toolbar" role="group" aria-label="Order alert sound">
                <button
                  type="button"
                  className={`realtime-sound-btn${soundMuted ? '' : ' is-active'}`}
                  onClick={toggleSoundMuted}
                  aria-pressed={!soundMuted}
                  aria-label={soundMuted ? 'Unmute order alerts' : 'Mute order alerts'}
                  title={soundMuted ? 'Unmute order alerts' : 'Mute order alerts'}
                >
                  {soundMuted ? (
                    <IconSpeakerMute size={20} />
                  ) : (
                    <IconSpeakerOn size={20} strokeWidth={2} />
                  )}
                </button>
                <span className="realtime-sound-divider" aria-hidden />
                <button
                  type="button"
                  className={`realtime-sound-btn realtime-sound-btn--test${soundBlocked ? ' needs-unlock' : ''}`}
                  onClick={() => void testOrderSound()}
                  aria-label="Test order alert sound"
                  title={soundBlocked ? 'Browser blocked sound — tap to unlock' : 'Play test chime'}
                >
                  <IconSpeakerMute size={20} />
                </button>
              </div>
            </div>
            <div className="app-topbar-end d-flex align-items-center gap-2 gap-lg-3 min-w-0">
              <StoreOpenSwitch />
              <div className="dropdown flex-shrink-0" ref={userMenuRef}>
              <button
                type="button"
                className="btn btn-light border-0 rounded-3 d-flex align-items-center gap-2 py-2 ps-2 pe-3 shadow-none app-user-trigger"
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
                id="adminUserMenu"
                onClick={() => setUserMenuOpen((open) => !open)}
              >
                {photo ? (
                  <img src={photo} alt="" className="app-user-avatar rounded-circle object-fit-cover" width={40} height={40} />
                ) : (
                  <span className="app-user-avatar app-user-avatar-initials rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold">
                    {initials}
                  </span>
                )}
                <span className="text-start d-none d-sm-block app-user-trigger-text min-w-0">
                  <span className="d-block fw-semibold text-dark text-truncate" title={displayName}>
                    {displayName}
                  </span>
                  <span className="d-block small text-secondary text-truncate" title={admin?.email}>
                    {admin?.email}
                  </span>
                </span>
                <IconChevronDown size={16} className="text-secondary flex-shrink-0" />
              </button>
              <ul
                className={`dropdown-menu dropdown-menu-end shadow border-0 py-2 mt-2${userMenuOpen ? ' show' : ''}`}
                aria-labelledby="adminUserMenu"
              >
                <li className="px-3 py-1 d-sm-none">
                  <div className="small fw-semibold text-dark text-truncate">{displayName}</div>
                  <div className="small text-secondary text-truncate">{admin?.email}</div>
                </li>
                <li className="d-sm-none">
                  <hr className="dropdown-divider my-2" />
                </li>
                <li>
                  <button type="button" className="dropdown-item d-flex align-items-center gap-2 text-danger" onClick={handleLogout}>
                    <IconLogOut size={18} />
                    Sign out
                  </button>
                </li>
              </ul>
              </div>
            </div>
          </header>
          <div className="app-main-content flex-grow-1 p-4 min-w-0">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
